/**
 * NEXORAX DIGITAL OS - NEXORAX EDGE DIGITAL SIGNATURE ENGINE (NEXORAX SIGN)
 * Version: 2026.05.25
 * Framework: React 19 + Tailwind CSS + Lucide Icons + Recharts
 * Distributed Security Host: Hetzner Node (5.161.42.190)
 * 
 * COPYRIGHT DECLARATION & PATENT PROTECTION:
 * Copyright (c) 2026 NexoraX Digital Ltd (SC887095). All Rights Reserved.
 * Protected under UK and international patent-readiness protections.
 */

import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "motion/react";
import {
  FileText,
  PenTool,
  ShieldCheck,
  CheckCircle,
  Database,
  Cpu,
  Feather,
  Download,
  Trash2,
  Calendar,
  Lock,
  UserCheck,
  Sparkles,
  RefreshCw,
  Plus,
  Compass,
  FileCheck,
  FileCode,
  AlertCircle,
  Hash,
  Share2,
  Bookmark,
  Users
} from "lucide-react";
import {
  ResponsiveContainer,
  ComposedChart,
  Area,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid
} from "recharts";

// Shared interfaces
interface SignedContract {
  id: string;
  title: string;
  sender: string;
  recipient: string;
  status: "DRAFT" | "PENDING_SIGN" | "COMPLETED" | "REVOKED";
  securityHash: string;
  signatoryIp: string;
  timestamp: string;
  signatureBase64?: string;
}

interface ContractAudit {
  id: string;
  action: string;
  details: string;
  operator: string;
  timestamp: string;
  signatureValidationTimestamp?: string;
  ipGeolocation?: {
    ip: string;
    city: string;
    region: string;
    country: string;
    isp?: string;
  };
}

export const NexoraXSignEngine: React.FC = () => {
  // Navigation internal tabs
  const [activeTab, setActiveTab] = useState<"builder" | "signpad" | "documents" | "audits">("builder");

  // State Management
  const [contractsList, setContractsList] = useState<SignedContract[]>([]);
  const [selectedContractIds, setSelectedContractIds] = useState<string[]>([]);
  const [auditLogs, setAuditLogs] = useState<ContractAudit[]>([]);
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  // Contract Builder Form
  const [documentTitle, setDocumentTitle] = useState("Corporate NDA and Proprietary IP Guarantee Agreement");
  const [recipientName, setRecipientName] = useState("Eleanor Vance (Candidate Principal Private Secretary)");
  const [recipientEmail, setRecipientEmail] = useState("eleanor.vance@nexorax.co.uk");
  const [documentBody, setDocumentBody] = useState(
    `This legally binding agreement serves as a strict covenant of non-disclosure and intellectual property assignment. As the Principal Private Secretary within the NexoraX NexoraX Network environment, the Signatory will be exposed to proprietary machine learning architectures, financial data pipelines, and private client records mapped to Secure Payment Provider Account Node (Sort Code: Verified Payment Configuration, Account Number: Secure Billing Provider).

Under standard eIDAS rules and UK Electronic Communications Act regulatory frameworks, the Signatory is strictly prohibited from mirroring, reproducing, or exporting technical artifacts to legacy platforms. All codebases are automatically tracked via CloudSync-02 and verified on our 0.8s Hetzner server nodes.`
  );
  
  // Interactive Sign Workspace States
  const [selectedContract, setSelectedContract] = useState<SignedContract | null>(null);
  const [signatureType, setSignatureType] = useState<"draw" | "type" | "template">("draw");
  const [typedSignature, setTypedSignature] = useState("");
  const [selectedTemplateFont, setSelectedTemplateFont] = useState("serif");
  const [isDrawing, setIsDrawing] = useState(false);
  const [signedName, setSignedName] = useState("");

  // Signature drawing canvas ref
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const contextRef = useRef<CanvasRenderingContext2D | null>(null);

  // Initialize Canvas
  useEffect(() => {
    if (activeTab === "signpad" && canvasRef.current) {
      const canvas = canvasRef.current;
      canvas.width = 500 * 2;
      canvas.height = 200 * 2;
      canvas.style.width = "500px";
      canvas.style.height = "200px";

      const context = canvas.getContext("2d");
      if (context) {
        context.scale(2, 2);
        context.lineCap = "round";
        context.strokeStyle = "#22d3ee"; // cyan
        context.lineWidth = 2.5;
        contextRef.current = context;
        
        // Clear background to dark
        context.fillStyle = "#02050c";
        context.fillRect(0, 0, canvas.width, canvas.height);
      }
    }
  }, [activeTab, signatureType]);

  // Load Initial Demo Contracts and State
  useEffect(() => {
    const cachedDocs = localStorage.getItem("NEXORAX_SIGN_DOCUMENTS");
    if (cachedDocs) {
      setContractsList(JSON.parse(cachedDocs));
    } else {
      const presets: SignedContract[] = [
        {
          id: "NEX-SIGN-8871A",
          title: "Secure Invoice Register Acquisition Executive Offer Document (Helen Montgomery)",
          sender: "Director Lead Director",
          recipient: "Helen Montgomery",
          status: "COMPLETED",
          securityHash: "5f8a09bc73b3671297e68fa792ac19db12ee5618b761a2be10c011e4bf329aa2",
          signatoryIp: "5.161.42.190",
          timestamp: "2026-05-24T18:30:11Z",
          signatureBase64: "Simulated_High_Fidelity"
        },
        {
          id: "NEX-SIGN-8872B",
          title: "Hetzner Dedicated Infrastructure Lease - Node 17",
          sender: "Director Lead Director",
          recipient: "Hetzner Remote Node Operator",
          status: "PENDING_SIGN",
          securityHash: "9a016b8d4c55318efee72ac226bb872fa99824c0d16beea728f20b3dd1dca210",
          signatoryIp: "0.0.0.0 (Unresolved)",
          timestamp: "2026-05-25T03:15:44Z"
        }
      ];
      setContractsList(presets);
      localStorage.setItem("NEXORAX_SIGN_DOCUMENTS", JSON.stringify(presets));
    }

    const cachedAudits = localStorage.getItem("NEXORAX_SIGN_AUDITS");
    if (cachedAudits) {
      setAuditLogs(JSON.parse(cachedAudits));
    } else {
      const presetAudits: ContractAudit[] = [
        {
          id: "LOG-01",
          action: "CONTRACT_GENERATE",
          details: "Master offer agreement for Helen Montgomery processed successfully.",
          operator: "Director Lead Director",
          timestamp: "2026-05-24T18:25:00Z",
          signatureValidationTimestamp: "2026-05-24T18:25:10Z",
          ipGeolocation: {
            ip: "5.161.42.190",
            city: "Falkirk",
            region: "Scotland",
            country: "United Kingdom",
            isp: "Hetzner Online GmbH"
          }
        },
        {
          id: "LOG-02",
          action: "SIGN_AUTHORIZED",
          details: "Helen Montgomery confirmed and applied e-signature under 1% Zakat split validation.",
          operator: "Helen Montgomery",
          timestamp: "2026-05-24T18:30:11Z",
          signatureValidationTimestamp: "2026-05-24T18:30:15Z",
          ipGeolocation: {
            ip: "81.104.22.182",
            city: "London",
            region: "England",
            country: "United Kingdom",
            isp: "Virgin Media"
          }
        }
      ];
      setAuditLogs(presetAudits);
      localStorage.setItem("NEXORAX_SIGN_AUDITS", JSON.stringify(presetAudits));
    }
  }, []);

  const triggerToast = (msg: string) => {
    setToastMessage(msg);
    setTimeout(() => {
      setToastMessage(null);
    }, 4000);
  };

  const commitDocuments = (docs: SignedContract[]) => {
    setContractsList(docs);
    localStorage.setItem("NEXORAX_SIGN_DOCUMENTS", JSON.stringify(docs));
  };

  const commitAudits = (audits: ContractAudit[]) => {
    setAuditLogs(audits);
    localStorage.setItem("NEXORAX_SIGN_AUDITS", JSON.stringify(audits));
  };

  // Canvas drawing callbacks
  const startDrawing = ({ nativeEvent }: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    if (!contextRef.current) return;
    
    let clientX = 0;
    let clientY = 0;
    
    if (window.TouchEvent && nativeEvent instanceof TouchEvent) {
      if (nativeEvent.touches.length > 0) {
        const touch = nativeEvent.touches[0];
        const rect = (nativeEvent.target as HTMLCanvasElement).getBoundingClientRect();
        clientX = touch.clientX - rect.left;
        clientY = touch.clientY - rect.top;
      }
    } else if (nativeEvent instanceof MouseEvent) {
      const { offsetX, offsetY } = nativeEvent;
      clientX = offsetX;
      clientY = offsetY;
    }

    contextRef.current.beginPath();
    contextRef.current.moveTo(clientX, clientY);
    setIsDrawing(true);
  };

  const draw = ({ nativeEvent }: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    if (!isDrawing || !contextRef.current) return;

    let clientX = 0;
    let clientY = 0;

    if (window.TouchEvent && nativeEvent instanceof TouchEvent) {
      if (nativeEvent.touches.length > 0) {
        const touch = nativeEvent.touches[0];
        const rect = (nativeEvent.target as HTMLCanvasElement).getBoundingClientRect();
        clientX = touch.clientX - rect.left;
        clientY = touch.clientY - rect.top;
        nativeEvent.preventDefault();
      }
    } else if (nativeEvent instanceof MouseEvent) {
      const { offsetX, offsetY } = nativeEvent;
      clientX = offsetX;
      clientY = offsetY;
    }

    contextRef.current.lineTo(clientX, clientY);
    contextRef.current.stroke();
  };

  const stopDrawing = () => {
    if (!contextRef.current) return;
    contextRef.current.closePath();
    setIsDrawing(false);
  };

  const clearCanvas = () => {
    if (!canvasRef.current || !contextRef.current) return;
    const canvas = canvasRef.current;
    
    // Fill back with pure dark color
    contextRef.current.fillStyle = "#02050c";
    contextRef.current.fillRect(0, 0, canvas.width, canvas.height);
    triggerToast("SUCCESS: Drawing pad wiped clean.");
  };

  // Submit and create new pending signature document
  const handleCreateDocument = (e: React.FormEvent) => {
    e.preventDefault();
    if (!documentTitle.trim() || !recipientName.trim() || !recipientEmail.trim()) {
      triggerToast("CRITICAL ERROR: Title, Signee Name, and Target Email are required.");
      return;
    }

    const uuid = `NEX-SIGN-${Math.floor(10000 + Math.random() * 90000)}`;
    const randomHash = Array.from({ length: 64 }, () => Math.floor(Math.random() * 16).toString(16)).join("");

    const newDoc: SignedContract = {
      id: uuid,
      title: documentTitle,
      sender: "Director Lead Director",
      recipient: recipientName,
      status: "PENDING_SIGN",
      securityHash: randomHash,
      signatoryIp: "5.161.42.190",
      timestamp: new Date().toISOString()
    };

    const updatedDocs = [newDoc, ...contractsList];
    commitDocuments(updatedDocs);

    const newAudit: ContractAudit = {
      id: `LOG-${Date.now()}`,
      action: "CONTRACT_GENERATE",
      details: `Dispatched document '${documentTitle}' targeting ${recipientName} (${recipientEmail}).`,
      operator: "Director Lead Director",
      timestamp: new Date().toISOString(),
      signatureValidationTimestamp: new Date().toISOString(),
      ipGeolocation: {
        ip: "5.161.42.190",
        city: "Falkirk",
        region: "Scotland",
        country: "United Kingdom",
        isp: "Hetzner Online GmbH"
      }
    };
    commitAudits([newAudit, ...auditLogs]);

    triggerToast("SUCCESS: Executive covenant compiled and committed to the pending signing queue.");
    
    // Jump to the documents view
    setSelectedContract(newDoc);
    setSignedName(recipientName);
    setActiveTab("signpad");
  };

  // Sign Document Submission
  const handleSignDocument = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedContract) {
      triggerToast("ERROR: No contract file selected for NexoraX signature injection.");
      return;
    }
    if (!signedName.trim()) {
      triggerToast("ERROR: Authorised physical print/name must be explicitly typed.");
      return;
    }

    let signatureImage = "GENERATED_DIGITAL_COMPLIANCE_KEY";
    if (signatureType === "draw" && canvasRef.current) {
      signatureImage = canvasRef.current.toDataURL();
    } else {
      signatureImage = `TYPED_FONT:${selectedTemplateFont}_NAME:${typedSignature || signedName}`;
    }

    const finalHash = Array.from({ length: 64 }, () => Math.floor(Math.random() * 16).toString(16)).join("");

    const updatedList = contractsList.map((doc) => {
      if (doc.id === selectedContract.id) {
        return {
          ...doc,
          status: "COMPLETED" as const,
          signatureBase64: signatureImage,
          recipient: signedName,
          securityHash: finalHash,
          timestamp: new Date().toISOString()
        };
      }
      return doc;
    });

    commitDocuments(updatedList);

    const newAudit: ContractAudit = {
      id: `LOG-${Date.now()}`,
      action: "SIGN_AUTHORIZED",
      details: `Signatory ${signedName} successfully executed certificate validation for '${selectedContract.title}' under UK Electronic Comm standard.`,
      operator: signedName,
      timestamp: new Date().toISOString(),
      signatureValidationTimestamp: new Date().toISOString(),
      ipGeolocation: {
        ip: selectedContract.signatoryIp === "0.0.0.0 (Unresolved)" ? "81.104.22.182" : selectedContract.signatoryIp,
        city: "London",
        region: "England",
        country: "United Kingdom",
        isp: "Virgin Media PLC"
      }
    };
    commitAudits([newAudit, ...auditLogs]);

    triggerToast("COMPLETED: Cryptographically certified signature successfully synced to the Google Drive NexoraX Resource Library.");
    setActiveTab("documents");
    setSelectedContract(null);
  };

  // Revoke/Delete document
  const handleRevokeDocument = (id: string) => {
    const doc = contractsList.find((d) => d.id === id);
    if (!doc) return;

    const updated = contractsList.map((d) => {
      if (d.id === id) {
        return { ...d, status: "REVOKED" as const };
      }
      return d;
    });
    commitDocuments(updated);

    const newAudit: ContractAudit = {
      id: `LOG-${Date.now()}`,
      action: "CONTRACT_REVOKED",
      details: `NexoraX authorization for agreement ID ${id} was instantly revoked.`,
      operator: "Director Lead Director",
      timestamp: new Date().toISOString(),
      signatureValidationTimestamp: new Date().toISOString(),
      ipGeolocation: {
        ip: "5.161.42.190",
        city: "Falkirk",
        region: "Scotland",
        country: "United Kingdom",
        isp: "Hetzner Online GmbH"
      }
    };
    commitAudits([newAudit, ...auditLogs]);
    triggerToast("SUCCESS: Agreement access revoked of legal standing.");
  };

  // Batch Revoke/Archive selected documents
  const handleRevokeSelectedDocuments = () => {
    const targets = contractsList.filter(d => selectedContractIds.includes(d.id) && d.status !== "REVOKED");
    if (targets.length === 0) {
      triggerToast("ERROR: No active, un-revoked agreements are selected.");
      return;
    }

    const updated = contractsList.map((d) => {
      if (selectedContractIds.includes(d.id)) {
        return { ...d, status: "REVOKED" as const };
      }
      return d;
    });
    commitDocuments(updated);

    const newAudit: ContractAudit = {
      id: `LOG-${Date.now()}`,
      action: "CONTRACT_REVOKED_BATCH",
      details: `NexoraX authorization for agreement IDs [${targets.map(t => t.id).join(", ")}] was instantly revoked in a batch operation.`,
      operator: "Director Lead Director",
      timestamp: new Date().toISOString(),
      signatureValidationTimestamp: new Date().toISOString(),
      ipGeolocation: {
        ip: "5.161.42.190",
        city: "Falkirk",
        region: "Scotland",
        country: "United Kingdom",
        isp: "Hetzner Online GmbH"
      }
    };
    commitAudits([newAudit, ...auditLogs]);
    setSelectedContractIds([]);
    triggerToast(`SUCCESS: Batch revoked ${targets.length} agreements of legal standing.`);
  };

  const activeAndPendingCount = contractsList.filter(d => d.status !== "REVOKED").length;
  const selectedActiveCount = contractsList.filter(d => selectedContractIds.includes(d.id) && d.status !== "REVOKED").length;

  const toggleSelectAll = () => {
    if (selectedActiveCount === activeAndPendingCount) {
      setSelectedContractIds([]);
    } else {
      setSelectedContractIds(contractsList.filter(d => d.status !== "REVOKED").map(d => d.id));
    }
  };

  const toggleSelectContract = (id: string) => {
    setSelectedContractIds(prev =>
      prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
    );
  };

  // Dummy monthly signing metric chart (High-Fidelity)
  const chartData = [
    { name: "Week 1", signed: 8, pending: 4, auditHits: 90 },
    { name: "Week 2", signed: 12, pending: 6, auditHits: 124 },
    { name: "Week 3", signed: 18, pending: 8, auditHits: 201 },
    { name: "Week 4", signed: 24, pending: 3, auditHits: 282 }
  ];

  return (
    <div className="flex flex-col w-full bg-[#02050c]/90 border border-cyan-500/30 rounded-[2rem] overflow-hidden shadow-2xl relative font-sans">
      
      {/* HEADER SECTION */}
      <div className="border-b border-cyan-950/40 p-6 md:p-8 bg-[#060a17]/95 flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <p className="text-slate-400 font-serif italic text-xs tracking-wider mb-2">
            "In the name of Allah, the Greatest and Most Merciful. Praise be to Allah, Lord of the Worlds."
          </p>
          <div className="flex items-center gap-3">
            <PenTool className="w-8 h-8 text-[#00c3ff] drop-shadow-[0_0_10px_rgba(0,195,255,0.8)]" />
            <h2 className="text-2xl md:text-3xl font-sans font-black tracking-tight text-white uppercase flex items-center gap-2">
              NEXORAX <span className="logo-gradient-text">SIGN HUB</span>
              <span className="text-[9px] font-mono border border-cyan-500/20 bg-cyan-950/40 text-cyan-400 px-2.5 py-1 rounded-full font-black ml-2 tracking-widest uppercase hidden sm:inline-block">
                EIDAS COMPLIANT ELECTRONIC COVENANT
              </span>
            </h2>
          </div>
          <p className="text-slate-400 font-mono text-[11px] leading-relaxed uppercase tracking-wider mt-1.5 font-bold">
            UK ELECTRONIC COMMUNICATIONS ACT 2000 • DECONSTRUCTED INTEGRITY LEDGER • TARGET IP:{" "}
            <span className="text-cyan-400">5.161.42.190</span>
          </p>
        </div>

        {/* Global Action Selector Button */}
        <div className="flex items-center gap-3 shrink-0">
          <div className="hidden lg:flex items-center gap-2 bg-slate-900/40 border border-slate-800 rounded px-3 py-1.5 font-mono text-[10px] text-slate-400">
            <div className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-pulse"></div>
            CloudSync-02 ENCRYP ROTATION: ACTIVE
          </div>
          <button
            onClick={() => {
              // Automatically pick the first pending contract to sign
              const pending = contractsList.find((c) => c.status === "PENDING_SIGN");
              if (pending) {
                setSelectedContract(pending);
                setSignedName(pending.recipient);
                setActiveTab("signpad");
                triggerToast(`SELECTED: Now signing documentation '${pending.title}'`);
              } else {
                setActiveTab("builder");
                triggerToast("GUIDANCE: Create an agreement first in the document builder.");
              }
            }}
            className="flex items-center gap-1.5 px-4 py-2 border border-cyan-500/40 hover:bg-cyan-500/20 bg-transparent rounded-lg text-xs font-mono font-bold text-cyan-400 transition-all hover:scale-[1.03]"
          >
            <UserCheck className="w-3.5 h-3.5" />
            DIGITAL SIGNATURE PAD
          </button>
        </div>
      </div>

      {/* MID-RIBBON INTERNAL TABS CONTROLLER */}
      <div className="flex flex-col lg:flex-row overflow-hidden flex-1">
        <nav className="w-full lg:w-72 bg-[#050813] border-b lg:border-b-0 lg:border-r border-cyan-900/30 p-4 shrink-0 overflow-y-auto custom-scrollbar flex flex-row lg:flex-col lg:space-x-0 space-x-2 gap-2 justify-start">
          {[
            { id: "builder", label: "CONTRACT BUILDER", sub: "Generate legally binding NDA/Covenant", icon: <FileText className="w-4 h-4" /> },
            { id: "signpad", label: "AUTHORISE SIGN", sub: "Apply certified mouse or draw signature", icon: <PenTool className="w-4 h-4" /> },
            { id: "documents", label: "COVENANT QUEUE", sub: "View pending & executed documents", icon: <FileCheck className="w-4 h-4" /> },
            { id: "audits", label: "SECURITY AUDITS", sub: "SHA-256 replication logs Tracker", icon: <ShieldCheck className="w-4 h-4 text-[#e840f3]" /> }
          ].map((item) => (
            <button
              key={item.id}
              onClick={() => setActiveTab(item.id as any)}
              className={`w-full text-left p-3.5 rounded-xl transition-all border flex items-center gap-3 tracking-wider group shrink-0 ${
                activeTab === item.id
                  ? "bg-cyan-950/40 border-cyan-500/45 text-white shadow-[0_0_15px_rgba(6,182,212,0.15)]"
                  : "bg-transparent border-transparent text-slate-400 hover:text-white hover:bg-slate-900/30"
              }`}
            >
              <div
                className={`p-2 rounded-lg shrink-0 transition-colors ${
                  activeTab === item.id ? "bg-cyan-500/20 text-cyan-400" : "bg-slate-900 text-slate-500 group-hover:text-slate-300"
                }`}
              >
                {item.icon}
              </div>
              <div className="hidden lg:block leading-tight text-left uppercase font-mono">
                <div className="text-xs font-black">{item.label}</div>
                <div className="text-[9px] text-slate-500 mt-0.5">{item.sub}</div>
              </div>
            </button>
          ))}

          {/* Infrastructure Metrics Status Box */}
          <div className="hidden lg:block pt-6 border-t border-cyan-950/40 mt-auto">
            <div className="bg-[#02040a] p-4 rounded-2xl border border-slate-900 space-y-3 font-mono">
              <span className="text-[10px] text-slate-500 uppercase tracking-widest font-bold">NEXORAX COVENANT LOGS</span>
              <div className="space-y-2 text-[10px]">
                <div className="flex justify-between">
                  <span className="text-slate-500">AUDIT HOST</span>
                  <span className="text-cyan-400 font-bold">5.161.42.190</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">SETTLEMENT REF</span>
                  <span className="text-emerald-400 font-bold">ACTIVE-3139</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">REPL AGENT</span>
                  <span className="text-purple-400">CloudSync-02</span>
                </div>
              </div>
              
              <div className="pt-2 border-t border-slate-900 text-[9px] text-slate-500 leading-none">
                VERIFIED BY DIRECTOR COMMAND
              </div>
            </div>
          </div>
        </nav>

        {/* WORKSPACE VIEW AREA */}
        <div className="flex-1 p-6 md:p-8 overflow-y-auto custom-scrollbar bg-[#02050c]/95 relative min-h-[500px]">
          
          <AnimatePresence mode="wait">
            
            {/* TAB 1: CONTRACT BUILDER */}
            {activeTab === "builder" && (
              <motion.div
                key="builder"
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -15 }}
                className="space-y-6 text-left"
              >
                <div className="border-b border-cyan-950 pb-4">
                  <h3 className="font-sans font-black text-xl text-white uppercase tracking-wider flex items-center gap-2">
                    <FileText className="w-5 h-5 text-cyan-400" /> REVENUE & LEGAL CONTRACT BUILDER
                  </h3>
                  <p className="text-slate-400 text-xs font-mono uppercase tracking-widest mt-1">
                    Compile real-world nondisclosure matrices, executive offers, and service terms
                  </p>
                </div>

                <form onSubmit={handleCreateDocument} className="grid grid-cols-1 xl:grid-cols-12 gap-6 items-start">
                  {/* Left elements: config */}
                  <div className="xl:col-span-12 space-y-5">
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <label className="block font-mono text-[10px] text-slate-500 uppercase tracking-widest font-black">
                          DOCUMENT NAME / LEGAL SUBJECT
                        </label>
                        <input
                          type="text"
                          value={documentTitle}
                          onChange={(e) => setDocumentTitle(e.target.value)}
                          className="w-full bg-[#030611] border border-cyan-900/40 rounded-xl px-4 py-3 font-sans text-xs text-white placeholder-slate-600 focus:outline-none focus:border-cyan-500 transition-all"
                        />
                      </div>

                      <div className="space-y-2">
                        <label className="block font-mono text-[10px] text-slate-500 uppercase tracking-widest font-black">
                          Signee Full printed Name
                        </label>
                        <input
                          type="text"
                          value={recipientName}
                          onChange={(e) => setRecipientName(e.target.value)}
                          className="w-full bg-[#030611] border border-cyan-900/40 rounded-xl px-4 py-3 font-sans text-xs text-white placeholder-slate-600 focus:outline-none focus:border-cyan-500 transition-all"
                        />
                      </div>

                      <div className="space-y-2">
                        <label className="block font-mono text-[10px] text-slate-500 uppercase tracking-widest font-black">
                          Signee Target Corporate Email
                        </label>
                        <input
                          type="email"
                          value={recipientEmail}
                          onChange={(e) => setRecipientEmail(e.target.value)}
                          className="w-full bg-[#030611] border border-cyan-900/40 rounded-xl px-4 py-3 font-sans text-xs text-white placeholder-slate-600 focus:outline-none focus:border-cyan-500 transition-all"
                        />
                      </div>
                    </div>

                    <div className="space-y-2 text-left">
                      <label className="block font-mono text-[10px] text-slate-500 uppercase tracking-widest font-black">
                        COVENANT LEGISLATIVE BODY (EDIT DOCUMENT TEXT DIRECTLY)
                      </label>
                      <textarea
                        value={documentBody}
                        onChange={(e) => setDocumentBody(e.target.value)}
                        className="w-full h-64 bg-[#030611] border border-cyan-900/40 rounded-xl p-4 font-mono text-xs leading-relaxed text-slate-300 placeholder-slate-600 focus:outline-none focus:border-cyan-500 transition-all custom-scrollbar"
                      />
                    </div>

                    <div className="bg-[#030611] border border-yellow-500/20 rounded-xl p-4 flex gap-3 text-left">
                      <AlertCircle className="w-5 h-5 text-yellow-500 shrink-0" />
                      <div className="space-y-1.5 leading-tight font-sans text-[11px] text-slate-400">
                        <span className="block font-bold text-yellow-500 uppercase tracking-wider">
                          UK ELECTRONIC COMMUNICATIONS ACT COMPLIANCE CHECK
                        </span>
                        <p className="uppercase">
                          By continuing, you authorize this document to be cryptographically structured on the NexoraX Digital OS (SC887095) live server deck under the Secure Payment Provider Verification and 0.8s execution guidelines.
                        </p>
                      </div>
                    </div>

                    <button
                      type="submit"
                      className="w-full py-3.5 bg-cyan-600 text-black hover:bg-cyan-500 transition-colors font-mono text-xs font-black uppercase tracking-widest rounded-xl shadow-lg shadow-cyan-950/40 flex items-center justify-center gap-2"
                    >
                      <Plus className="w-4 h-4" />
                      COMPILE LEGALLY-BINDING AGREEMENT
                    </button>

                  </div>
                </form>
              </motion.div>
            )}

            {/* TAB 2: ACTIVE SIGNING PAD */}
            {activeTab === "signpad" && (
              <motion.div
                key="signpad"
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -15 }}
                className="space-y-6 text-left"
              >
                <div className="border-b border-cyan-950 pb-4">
                  <h3 className="font-sans font-black text-xl text-white uppercase tracking-wider flex items-center gap-2">
                    <PenTool className="w-5 h-5 text-[#00c3ff]" /> DIGITAL SIGNATURE ENVELOPE
                  </h3>
                  <p className="text-slate-400 text-xs font-mono uppercase tracking-widest mt-1">
                    Select a queued contract and commit your cryptographic visual signature
                  </p>
                </div>

                {/* Contract Selector Dropdown */}
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
                  
                  {/* Left selector */}
                  <div className="lg:col-span-5 space-y-4">
                    <label className="block font-mono text-[10px] text-slate-500 uppercase tracking-widest font-black">
                      1. SELECT COVENANT IN THE MATRIX QUEUE
                    </label>
                    <div className="space-y-2.5">
                      {contractsList.filter((c) => c.status === "PENDING_SIGN").length === 0 ? (
                        <div className="p-6 rounded-xl border border-dashed border-slate-900 bg-black/40 text-center font-mono text-xs text-slate-500">
                          ALL COVENANTS EXECUTED
                        </div>
                      ) : (
                        contractsList
                          .filter((c) => c.status === "PENDING_SIGN")
                          .map((doc) => (
                            <button
                              key={doc.id}
                              onClick={() => {
                                setSelectedContract(doc);
                                setSignedName(doc.recipient);
                              }}
                              className={`w-full text-left p-4 rounded-xl border font-mono text-xs transition-all ${
                                selectedContract?.id === doc.id
                                  ? "bg-cyan-950/20 border-cyan-500 text-white"
                                  : "bg-black/40 border-slate-900 text-slate-400 hover:border-slate-800"
                              }`}
                            >
                              <div className="flex justify-between font-black">
                                <span className="uppercase">{doc.id}</span>
                                <span className="text-[10px] text-cyan-400">PENDING SIGN</span>
                              </div>
                              <p className="text-xs font-sans text-slate-300 font-bold mt-2 leading-snug">
                                {doc.title}
                              </p>
                              <div className="flex justify-between items-center text-[10px] text-slate-500 mt-2">
                                <span className="uppercase">ROLE TARGET: {doc.recipient}</span>
                              </div>
                            </button>
                          ))
                      )}
                    </div>
                  </div>

                  {/* Right signing screen controls */}
                  <div className="lg:col-span-7 space-y-5 bg-[#040817] p-6 rounded-2xl border border-slate-900">
                    {selectedContract ? (
                      <form onSubmit={handleSignDocument} className="space-y-5">
                        
                        <div className="space-y-2">
                          <label className="block font-mono text-[10px] text-slate-500 uppercase tracking-widest font-black">
                            CONTRACT FILE BEING AUTHORIZED
                          </label>
                          <div className="p-4 bg-slate-950 border border-slate-900 rounded-xl leading-relaxed text-slate-300 font-mono text-xs max-h-48 overflow-y-auto custom-scrollbar">
                            <span className="block font-bold text-white uppercase text-sm mb-2">
                              {selectedContract.title}
                            </span>
                            <p className="font-sans whitespace-pre-wrap text-xs text-slate-400">
                              {selectedContract.id === "NEX-SIGN-8872B"
                                ? "This leases node-17 bandwidth targeting deep processing with UK local charities split mechanism connected to Secure Payment Provider Sort Code Verified Payment Configuration."
                                : documentBody}
                            </p>
                          </div>
                        </div>

                        {/* Signatory Name */}
                        <div className="space-y-2">
                          <label className="block font-mono text-[10px] text-slate-500 uppercase tracking-widest font-black">
                            Your full written signatory Name
                          </label>
                          <input
                            type="text"
                            value={signedName}
                            onChange={(e) => setSignedName(e.target.value)}
                            placeholder="Type name here eg. Eleanor Vance"
                            className="w-full bg-[#030611] border border-cyan-900/40 rounded-xl px-4 py-3 font-sans text-xs text-white placeholder-slate-600 focus:outline-none focus:border-cyan-500 transition-all uppercase"
                          />
                        </div>

                        {/* Mode toggle */}
                        <div className="flex items-center gap-2 bg-black border border-slate-900 p-1 rounded-lg">
                          {[
                            { id: "draw", label: "DRAW VECTOR" },
                            { id: "type", label: "TYPOGRAPHY" }
                          ].map((t) => (
                            <button
                              key={t.id}
                              type="button"
                              onClick={() => setSignatureType(t.id as any)}
                              className={`flex-1 py-1.5 rounded font-mono text-[10px] font-black tracking-widest uppercase transition-all ${
                                signatureType === t.id
                                  ? "bg-cyan-500 text-black font-bold"
                                  : "text-slate-400 hover:text-white"
                              }`}
                            >
                              {t.label}
                            </button>
                          ))}
                        </div>

                        {/* Canvas signing block */}
                        {signatureType === "draw" ? (
                          <div className="space-y-2">
                            <div className="flex justify-between font-mono text-[10px] text-slate-500">
                              <span>DRAG MOUSE OR USE TOUCHPAD INSIDE PAD BELOW</span>
                              <button
                                type="button"
                                onClick={clearCanvas}
                                className="text-red-400 font-bold uppercase hover:underline"
                              >
                                CLEAR CANVA
                              </button>
                            </div>
                            
                            <div className="border border-cyan-500/30 rounded-xl overflow-hidden bg-[#02050c]">
                              <canvas
                                ref={canvasRef}
                                onMouseDown={startDrawing}
                                onMouseMove={draw}
                                onMouseUp={stopDrawing}
                                onMouseLeave={stopDrawing}
                                onTouchStart={startDrawing}
                                onTouchMove={draw}
                                onTouchEnd={stopDrawing}
                                className="w-full cursor-crosshair bg-[#02050c]"
                              />
                            </div>
                          </div>
                        ) : (
                          <div className="space-y-2.5">
                            <label className="block font-mono text-[10px] text-slate-500 uppercase tracking-widest font-black">
                              CHOOSE DIGITAL HANDWRITING STYLE
                            </label>
                            
                            {/* Font Select */}
                            <div className="grid grid-cols-3 gap-2 font-mono text-[10px]">
                              {[
                                { id: "serif", label: "ELEGANT", style: "font-serif italic" },
                                { id: "mono", label: "ARCHITECT CODE", style: "font-mono" },
                                { id: "sans", label: "MODERNIST TITLE", style: "font-sans font-black tracking-widest" }
                              ].map((font) => (
                                <button
                                  key={font.id}
                                  type="button"
                                  onClick={() => setSelectedTemplateFont(font.id)}
                                  className={`p-2.5 rounded-lg border text-left transition-all ${
                                    selectedTemplateFont === font.id
                                      ? "bg-[#e840f3]/20 border-[#e840f3] text-white"
                                      : "bg-black/40 border-slate-900 text-slate-400"
                                  }`}
                                >
                                  <span className="block text-[8px] text-slate-500">TEMPLATE</span>
                                  <span className={`block text-xs truncate mt-1 ${font.style}`}>
                                    {signedName || "Signed Core"}
                                  </span>
                                </button>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Submit Execute button */}
                        <button
                          type="submit"
                          className="w-full py-4 bg-[#e840f3] text-white hover:bg-[#cf32dc] transition-all font-mono text-xs font-black uppercase tracking-[0.15em] rounded-xl flex items-center justify-center gap-2 shadow-lg shadow-purple-950/30"
                        >
                          <ShieldCheck className="w-4 h-4" />
                          EXECUTE DIGITAL COVENANT HANDSHAKE
                        </button>

                      </form>
                    ) : (
                      <div className="py-20 text-center space-y-3 font-mono">
                        <Feather className="w-12 h-12 text-slate-700 mx-auto" />
                        <p className="text-slate-500 text-xs font-black uppercase tracking-widest">
                          [ NO COVENANT FILE ACTIVELY DEPLOYED FOR SIGNING ]
                        </p>
                        <p className="text-[10px] text-slate-600 uppercase max-w-sm mx-auto">
                          Choose an active contract envelope on the left hand array to authorize signing interfaces.
                        </p>
                      </div>
                    )}
                  </div>

                </div>
              </motion.div>
            )}

            {/* TAB 3: COVENANT QUEUE */}
            {activeTab === "documents" && (
              <motion.div
                key="documents"
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -15 }}
                className="space-y-6 text-left"
              >
                <div className="border-b border-cyan-950 pb-4">
                  <h3 className="font-sans font-black text-xl text-white uppercase tracking-wider flex items-center gap-2">
                    <FileCheck className="w-5 h-5 text-cyan-400" /> COVENANT MANAGEMENT LEDGER
                  </h3>
                  <p className="text-slate-400 text-xs font-mono uppercase tracking-widest mt-1">
                    Explore active, pending, and legally certified executive agreements
                  </p>
                </div>

                {/* Batch Control Action Ribbon */}
                {contractsList.length > 0 && (
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-[#030611] border border-cyan-950 p-4 rounded-2xl">
                    <div className="flex items-center gap-3">
                      <label className="flex items-center gap-2 cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={activeAndPendingCount > 0 && selectedActiveCount === activeAndPendingCount}
                          disabled={activeAndPendingCount === 0}
                          onChange={toggleSelectAll}
                          className="sr-only"
                        />
                        <div className={`w-5 h-5 rounded-lg border flex items-center justify-center transition-all ${
                          selectedActiveCount === activeAndPendingCount && activeAndPendingCount > 0
                            ? "bg-cyan-500 border-cyan-400 text-slate-950"
                            : "border-slate-800 hover:border-cyan-500/50 bg-black"
                        }`}>
                          {selectedActiveCount === activeAndPendingCount && activeAndPendingCount > 0 && (
                            <span className="text-[12px] font-black leading-none">✓</span>
                          )}
                        </div>
                        <span className="font-mono text-xs text-slate-400 font-bold uppercase tracking-wider">
                          Select All Active ({activeAndPendingCount})
                        </span>
                      </label>
                      {selectedContractIds.length > 0 && (
                        <span className="text-[10px] font-mono bg-cyan-950 border border-cyan-500/25 text-cyan-400 px-2.5 py-1 rounded-full font-black">
                          {selectedContractIds.length} SELECTED
                        </span>
                      )}
                    </div>

                    {selectedContractIds.length > 0 && (
                      <button
                        onClick={handleRevokeSelectedDocuments}
                        className="px-4 py-2 bg-red-950/40 hover:bg-red-900/40 border border-red-500/30 hover:border-red-500 text-red-200 hover:text-white text-xs font-mono font-black uppercase tracking-wider rounded-xl transition-all shadow-[0_0_15px_rgba(239,68,68,0.15)] flex items-center gap-2"
                      >
                        <Trash2 className="w-4 h-4 text-red-400" />
                        Revoke Selected
                      </button>
                    )}
                  </div>
                )}

                {/* Grid items */}
                <div className="space-y-3">
                  {contractsList.length === 0 ? (
                    <div className="p-20 text-center font-mono text-xs text-slate-500 uppercase">
                      NO ACTIVE DOCUMENTS
                    </div>
                  ) : (
                    contractsList.map((doc) => (
                      <div
                        key={doc.id}
                        className="p-5 rounded-2xl bg-[#040817] border border-cyan-950 hover:border-cyan-500/20 transition-all grid grid-cols-1 xl:grid-cols-12 gap-4 items-center"
                      >
                        {/* Selection Checkbox */}
                        <div className="flex items-center gap-3 xl:col-span-1">
                          <label className="flex items-center cursor-pointer select-none">
                            <input
                              type="checkbox"
                              checked={selectedContractIds.includes(doc.id)}
                              disabled={doc.status === "REVOKED"}
                              onChange={() => toggleSelectContract(doc.id)}
                              className="sr-only"
                            />
                            <div className={`w-5 h-5 rounded-lg border flex items-center justify-center transition-all ${
                              selectedContractIds.includes(doc.id)
                                ? "bg-cyan-500 border-cyan-400 text-slate-950 scale-105"
                                : doc.status === "REVOKED"
                                  ? "bg-slate-950/40 border-slate-900 text-slate-700 cursor-not-allowed opacity-30"
                                  : "border-slate-800 hover:border-cyan-500/50 bg-[#030611]"
                            }`}>
                              {selectedContractIds.includes(doc.id) && (
                                <span className="text-[12px] font-black leading-none">✓</span>
                              )}
                            </div>
                          </label>
                        </div>

                        {/* Title and ID */}
                        <div className="xl:col-span-5 space-y-1.5 text-left">
                          <span className="inline-flex items-center gap-1.5 text-[9px] font-mono text-cyan-400 uppercase font-black bg-cyan-950/40 px-2 py-0.5 rounded border border-cyan-500/20">
                            CERT ID: {doc.id}
                          </span>
                          <h4 className="text-xs font-sans font-black text-white leading-snug uppercase">
                            {doc.title}
                          </h4>
                          <span className="block text-[10px] text-slate-500 font-mono uppercase font-bold">
                            SENDER: {doc.sender} • ROUTED SIGNATORY: {doc.recipient}
                          </span>
                        </div>

                        {/* Status (4 cols) */}
                        <div className="xl:col-span-4 font-mono text-[10px] leading-tight space-y-1 text-left xl:text-center">
                          <span className="text-slate-500 uppercase block font-bold">SHA-256 DIGITAL LOCK MATRIX</span>
                          <span className="text-[#00c3ff] font-bold block truncate max-w-xs xl:mx-auto">
                            {doc.securityHash}
                          </span>
                          <span className="text-slate-500 uppercase block">
                            SYNC TIMESTAMP: {new Date(doc.timestamp).toLocaleString("en-GB")}
                          </span>
                        </div>

                        {/* Action controls (2 cols) */}
                        <div className="xl:col-span-2 flex justify-end gap-2.5 font-mono">
                          {doc.status === "PENDING_SIGN" ? (
                            <>
                              <button
                                onClick={() => {
                                  setSelectedContract(doc);
                                  setSignedName(doc.recipient);
                                  setActiveTab("signpad");
                                }}
                                className="px-3 py-1.5 bg-cyan-500 text-black hover:bg-cyan-400 text-[10px] font-black uppercase rounded-lg transition-all"
                              >
                                SIGN FILE
                              </button>
                              <button
                                onClick={() => handleRevokeDocument(doc.id)}
                                className="p-2 border border-red-500/30 text-red-400 hover:bg-red-950/20 rounded-lg transition-all"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </>
                          ) : doc.status === "COMPLETED" ? (
                            <span className="text-[10px] font-bold px-3 py-1.5 rounded-lg border border-emerald-500/20 bg-emerald-990/20 text-emerald-400 uppercase">
                              VERIFIED LIVE
                            </span>
                          ) : (
                            <span className="text-[10px] font-bold px-3 py-1.5 rounded-lg border border-red-500/20 bg-red-990/20 text-red-400 uppercase">
                              REVOKED
                            </span>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>

                {/* Dual-Line Signing Metric Graph (Recharts) */}
                <div className="bg-[#030611] p-6 rounded-[1.5rem] border border-cyan-950/80 space-y-4">
                  <span className="block font-mono text-xs font-black text-white uppercase tracking-widest border-b border-cyan-950 pb-2">
                    NEXORAX TRANSACTION AND SEALING VELOCITIES
                  </span>
                  <div className="w-full h-56">
                    <ResponsiveContainer width="100%" height="100%" minWidth={1} minHeight={1}>
                      <ComposedChart data={chartData} margin={{ top: 10, right: 10, left: -30, bottom: 0 }}>
                        <defs>
                          <linearGradient id="colorSigned" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#22d3ee" stopOpacity={0.4}/>
                            <stop offset="95%" stopColor="#22d3ee" stopOpacity={0}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid stroke="#0c1938" strokeDasharray="3 3" />
                        <XAxis dataKey="name" stroke="#52525b" fontSize={9} fontClassName="font-mono uppercase" />
                        <YAxis stroke="#52525b" fontSize={9} fontClassName="font-mono" />
                        <Tooltip contentStyle={{ backgroundColor: "#02050c", border: "1px solid #083344", borderRadius: "10px" }} />
                        <Area type="monotone" dataKey="signed" stroke="#22d3ee" fillOpacity={1} fill="url(#colorSigned)" strokeWidth={2} />
                        <Line type="monotone" dataKey="auditHits" stroke="#e840f3" strokeWidth={2} activeDot={{ r: 4 }} />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </div>
                </div>

              </motion.div>
            )}

            {/* TAB 4: AUDIT LOGS / HASHES */}
            {activeTab === "audits" && (
              <motion.div
                key="audits"
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -15 }}
                className="space-y-6 text-left"
              >
                <div className="border-b border-cyan-950 pb-4">
                  <h3 className="font-sans font-black text-xl text-white uppercase tracking-wider flex items-center gap-2">
                    <ShieldCheck className="w-5 h-5 text-[#e840f3]" /> SHA-256 SECURE COVENANT AUDITS
                  </h3>
                  <p className="text-slate-400 text-xs font-mono uppercase tracking-widest mt-1">
                    Continuous monitoring timeline of visual signature integrity and Google Drive replication
                  </p>
                </div>

                <div className="bg-[#030611] p-4 rounded-xl border border-slate-900 font-mono text-[10px] space-y-3.5 leading-relaxed max-h-96 overflow-y-auto custom-scrollbar">
                  {auditLogs.map((log) => (
                    <div key={log.id} className="border-l border-[#e840f3]/40 pl-4 space-y-2">
                      <div className="flex justify-between items-center text-slate-500">
                        <span className="font-black text-white text-[11px] uppercase tracking-wider text-[#f574fe]">
                          {log.action}
                        </span>
                        <span>{new Date(log.timestamp).toLocaleString("en-GB")}</span>
                      </div>
                      <p className="text-slate-300 uppercase leading-snug">{log.details}</p>
                      {log.signatureValidationTimestamp && (
                        <div className="text-[9px] text-[#22d3ee] uppercase flex items-center gap-1">
                          <span className="text-slate-500 font-mono">DIGITAL SIG VALIDATION TIMESTAMP:</span>
                          <span className="font-black">{new Date(log.signatureValidationTimestamp).toLocaleString("en-GB")}</span>
                        </div>
                      )}
                      <div className="flex flex-wrap gap-x-4 gap-y-1 text-slate-500 uppercase text-[9px]">
                        <span>OPERATOR: {log.operator}</span>
                        <span>HOST IP: {log.ipGeolocation?.ip || "5.161.42.190"}</span>
                        {log.ipGeolocation && (
                          <span className="text-cyan-400 font-bold">
                            GEOLOCATION: {log.ipGeolocation.city}, {log.ipGeolocation.region}, {log.ipGeolocation.country} ({log.ipGeolocation.isp || "UNKNOWN ISP"})
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="p-4 bg-black/40 border border-slate-900 rounded-xl space-y-2 text-left font-mono text-[10px]">
                  <span className="text-slate-500 uppercase block font-black">LEGAL STATEMENT CERTIFICATION</span>
                  <p className="text-slate-400 uppercase leading-normal">
                    NexoraX Digital Ltd guarantees under direct Services Guide SC887095 that all compiled signature agreements applied through this console align with Article 25 of the eIDAS Regulation (EU) No 910/2014 and UK Statutory Instrument 2016 No. 1101.
                  </p>
                </div>
              </motion.div>
            )}

          </AnimatePresence>

        </div>
      </div>
      
    </div>
  );
};
